import { useEffect } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CustomSurfaceTreatmentsDto } from "#modules/companySettings/infra/fetchCustomSurfaceTreatments.api";
import { useCreateCustomSurfaceTreatment } from "#modules/companySettings/view/useCreateCustomSurfaceTreatment";
import { useUpdateCustomSurfaceTreatment } from "#modules/companySettings/view/useUpdateCustomSurfaceTreatment";
import { Group } from "#shared/view/components/Group";
import { Modal } from "#shared/view/components/Modal";
import { Stack } from "#shared/view/components/Stack";
import { Button } from "#shared/view/designSystem/Button";
import { NativeSelectInputController } from "#shared/view/designSystem/inputs/NativeSelectInputController";
import { NumberInputController } from "#shared/view/designSystem/inputs/NumberInputController";
import { TextInputController } from "#shared/view/designSystem/inputs/TextInputController";
import {
  assertUnreachable,
  CompanySurfaceTreatment,
  SurfaceTreatmentMethodSchema,
  SurfaceTreatmentMethodType,
  SurfaceTreatmentTypeSchema,
  SurfaceTreatmentTypeType,
} from "shared";

import {
  getSurfaceTreatmentPriceSuffix,
  surfaceTreatmentMethodOptions,
  surfaceTreatmentTypeOptions,
} from "./surfaceTreatmentLabels";

type FormData = {
  description: string;
  type: SurfaceTreatmentTypeType;
  method: SurfaceTreatmentMethodType;
  price: number | null;
  formula: string | null;
  preparationTimeFormula: string | null;
  cycleTimeFormula: string | null;
};

type CommonProps = {
  opened: boolean;
  onClose: () => void;
  companyId: string;
};

type CreateProps = CommonProps & {
  mode: "create";
  initialData?: undefined;
};

type EditProps = CommonProps & {
  mode: "edit";
  initialData: CompanySurfaceTreatment;
};

type Props = CreateProps | EditProps;

const getDefaultValues = (initialData?: CustomSurfaceTreatmentsDto[number]): FormData => ({
  description: initialData?.description ?? "",
  type: initialData?.type ?? SurfaceTreatmentTypeSchema.enum.INHOUSE,
  method: initialData?.method ?? SurfaceTreatmentMethodSchema.enum.fixed,
  price: initialData?.price ? initialData.price / 100 : 0,
  formula: initialData?.formula ?? null,
  preparationTimeFormula: initialData?.preparationTimeFormula ?? null,
  cycleTimeFormula: initialData?.cycleTimeFormula ?? null,
});

export const SurfaceTreatmentFormModal = ({
  opened,
  onClose,
  companyId,
  mode,
  initialData,
}: Props) => {
  const { t } = useTranslation();
  const { createCustomSurfaceTreatment, isPending: isCreating } = useCreateCustomSurfaceTreatment();
  const { updateCustomSurfaceTreatment, isPending: isUpdating } = useUpdateCustomSurfaceTreatment();

  const methods = useForm<FormData>({
    defaultValues: getDefaultValues(initialData),
  });

  const { control, handleSubmit, reset } = methods;
  const method = useWatch({ control, name: "method" });

  useEffect(() => {
    if (opened) {
      reset(getDefaultValues(initialData));
    }
  }, [opened, initialData, reset]);

  const handleClose = () => {
    reset(getDefaultValues());
    onClose();
  };

  const onSubmit = (data: FormData) => {
    const isFormula = data.method === SurfaceTreatmentMethodSchema.enum.formula;
    const isTime = data.method === SurfaceTreatmentMethodSchema.enum.time;

    const payload = {
      description: data.description,
      type: data.type,
      method: data.method,
      price: isFormula ? null : Math.round((data.price ?? 0) * 100),
      formula: isFormula ? (data.formula ?? null) : null,
      preparationTimeFormula: isTime ? (data.preparationTimeFormula ?? null) : null,
      cycleTimeFormula: isTime ? (data.cycleTimeFormula ?? null) : null,
    };

    switch (mode) {
      case "create":
        createCustomSurfaceTreatment({ companyId, payload }, { onSuccess: handleClose });
        break;
      case "edit":
        updateCustomSurfaceTreatment(
          { companyId, surfaceTreatmentId: initialData.id, payload },
          { onSuccess: handleClose },
        );
        break;
      default:
        assertUnreachable(mode);
    }
  };

  const isPending = isCreating || isUpdating;
  const isFormula = method === SurfaceTreatmentMethodSchema.enum.formula;
  const isTime = method === SurfaceTreatmentMethodSchema.enum.time;
  const priceSuffix = getSurfaceTreatmentPriceSuffix(t, method);
  const priceLabel = priceSuffix
    ? `${t("settings.surfaceTreatments.price")} (${priceSuffix})`
    : t("settings.surfaceTreatments.price");
  const methodOptions = surfaceTreatmentMethodOptions(t);
  const typeOptions = surfaceTreatmentTypeOptions(t);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        mode === "create"
          ? t("settings.surfaceTreatments.createModal.title")
          : t("settings.surfaceTreatments.editModal.title")
      }
      size="md"
    >
      <FormProvider {...methods}>
        <form
          onSubmit={(e) => {
            e.stopPropagation();
            void handleSubmit(onSubmit)(e);
          }}
        >
          <Stack gap="md">
            <TextInputController
              name="description"
              label={t("settings.surfaceTreatments.description")}
              control={control}
              required
            />

            <NativeSelectInputController
              name="type"
              label={t("settings.surfaceTreatments.type")}
              data={typeOptions}
              withEmptyData={false}
              required
            />

            <NativeSelectInputController
              name="method"
              label={t("settings.surfaceTreatments.method")}
              data={methodOptions}
              withEmptyData={false}
              required
            />

            {!isFormula && (
              <NumberInputController
                name="price"
                label={priceLabel}
                control={control}
                min={0}
                required
                precision={2}
              />
            )}

            {isFormula && (
              <TextInputController
                name="formula"
                label={t("settings.surfaceTreatments.formula")}
                control={control}
                required
              />
            )}

            {isTime && (
              <>
                <TextInputController
                  name="preparationTimeFormula"
                  label={t("settings.surfaceTreatments.preparationTimeFormula")}
                  control={control}
                  required
                />
                <TextInputController
                  name="cycleTimeFormula"
                  label={t("settings.surfaceTreatments.cycleTimeFormula")}
                  control={control}
                  required
                />
              </>
            )}

            <Group justify="flex-end" mt="md">
              <Button variant="outline" onClick={handleClose} disabled={isPending}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={isPending}>
                {mode === "create" ? t("common.add") : t("common.validate")}
              </Button>
            </Group>
          </Stack>
        </form>
      </FormProvider>
    </Modal>
  );
};
